import React, { useCallback, useMemo, useState } from 'react';
import { Button, Form, Modal, ModalProps } from 'react-bootstrap';
import { Auth, CognitoUser } from '@aws-amplify/auth';

import config from '@/lib/config';
import { ACTION_STATUS, ACTION_TYPES, MFA_TYPES, PREFERRED_MFA_TYPES } from '@/lib/models';
import { trackAction, toErrorFields } from '@/lib/utils';
import { usePlatformsContext } from '@/hooks';
import { LoadingButton, OtpInput } from '@/components';
import RememberDeviceField from './remember-device-field';
import { ReactComponent as ContactUsIcon } from '@/assets/icons/contact-us.svg';
import { ReactComponent as SendIcon } from '@/assets/icons/send-icon.svg';
import { ReactComponent as ExclamationIcon } from '@/assets/icons/exclamation-icon.svg';

const DialogAs = (dialogProps: any) => (
	<Modal.Dialog
		{...dialogProps}
		style={{ maxWidth: 'none', width: 'fit-content', marginLeft: 'auto', marginRight: 'auto' }}
	/>
);

interface MfaSmsCodeModalProps extends ModalProps {
	fauxSignedInUser?: CognitoUser;
	onBack(): void;
	onSuccess(user: CognitoUser): void;
	onResendCode?(): Promise<CognitoUser | undefined> | CognitoUser | undefined;
}

export const MfaSmsCodeModal = ({
	fauxSignedInUser,
	onBack,
	onSuccess,
	onResendCode,
	...props
}: MfaSmsCodeModalProps) => {
	const [isLoading, setIsLoading] = useState(false);
	const [codeInputValue, setCodeInputValue] = useState('');
	const [codeMessage, setCodeMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
	const [currentFauxUser, setCurrentFauxUser] = useState<CognitoUser | undefined>(fauxSignedInUser);
	const { platforms } = usePlatformsContext();
	const platform = useMemo(() => platforms[0], [platforms]);

	const handleOnEnter = useCallback(() => {
		setIsLoading(false);
		setCodeInputValue('');
		setCodeMessage(null);
		setCurrentFauxUser(fauxSignedInUser);
	}, [fauxSignedInUser]);

	async function submitCode(rawCode: string) {
		setIsLoading(true);
		setCodeMessage(null);

		try {
			const currentUser: CognitoUser = await Auth.currentAuthenticatedUser();
			await Auth.verifyUserAttributeSubmit(currentUser, 'phone_number', rawCode.replace(/\s/g, ''));
			await Auth.setPreferredMFA(currentUser, MFA_TYPES.SMS);
			onSuccess(currentUser);
		} catch (userUnauthenticatedError) {
			try {
				const cognitoUser: CognitoUser = await Auth.confirmSignIn(
					currentFauxUser,
					rawCode.replace(/\s/g, ''),
					PREFERRED_MFA_TYPES.SMS_MFA
				);
				onSuccess(cognitoUser);
			} catch (confirmSignInError) {
				trackAction({
					actionType: ACTION_TYPES.MFA_REQUEST_SMS,
					status: ACTION_STATUS.FAILURE,
					...toErrorFields(confirmSignInError),
				});
				setCodeMessage({
					kind: 'error',
					text: 'The code is invalid please try again or send a new code',
				});
			}
		} finally {
			setIsLoading(false);
		}
	}

	function handleFormSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		submitCode(codeInputValue);
	}

	async function handleResendCode() {
		try {
			setIsLoading(true);
			setCodeMessage(null);
			if (onResendCode) {
				const refreshed = await onResendCode();
				if (refreshed) setCurrentFauxUser(refreshed);
			}
			trackAction({
				actionType: ACTION_TYPES.MFA_REQUEST_SMS_NEW_CODE,
				status: ACTION_STATUS.SUCCESS,
				emailAddress: currentFauxUser?.getUsername?.(),
			});
			setCodeInputValue('');
			setCodeMessage({ kind: 'success', text: 'A new verification code has been sent via SMS.' });
		} catch (error) {
			trackAction({
				actionType: ACTION_TYPES.MFA_REQUEST_SMS_NEW_CODE,
				status: ACTION_STATUS.FAILURE,
				emailAddress: currentFauxUser?.getUsername?.(),
				...toErrorFields(error),
			});
			setCodeMessage({ kind: 'error', text: (error as Error).message || 'Unable to send a new code.' });
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<Modal centered onEnter={handleOnEnter} dialogAs={DialogAs} {...props}>
			<Modal.Header>
				<Modal.Title>Set up SMS MFA</Modal.Title>
			</Modal.Header>
			<Form onSubmit={handleFormSubmit}>
				<Modal.Body style={{ paddingTop: 20, paddingBottom: 20 }}>
					<p className="mb-3">
						We have delivered the authentication code via SMS. <br />
						Please enter the code to complete authentication.
					</p>
					<Form.Group className="mb-0">
						<Form.Label>Verification Code</Form.Label>
						<div className="d-flex align-items-center" style={{ gap: 24 }}>
							<OtpInput
								value={codeInputValue}
								onChange={(next) => {
									setCodeInputValue(next);
									if (codeMessage) setCodeMessage(null);
								}}
								onComplete={(code) => submitCode(code)}
								autoFocus
								name="sms-setup-code"
								hasError={codeMessage?.kind === 'error'}
							/>
							<Button
								variant="link"
								onClick={handleResendCode}
								className="d-flex align-items-center p-0"
								style={{ gap: 8 }}
								disabled={isLoading}
							>
								<SendIcon />
								Send a New Code
							</Button>
						</div>
					</Form.Group>
					{config.HACKETT_USER_AUTH_REMEMBER_DEVICE_ENABLED && <RememberDeviceField />}
					{codeMessage && (
						<div
							className={`d-flex align-items-center mt-2 mb-0 ${
								codeMessage.kind === 'error' ? 'text-danger' : ''
							}`}
							style={{ gap: 8, fontSize: 14 }}
						>
							{codeMessage.kind === 'error' ? <ExclamationIcon /> : <SendIcon />}
							<span>{codeMessage.text}</span>
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex align-items-center w-100" style={{ gap: 8 }}>
						<a
							href={`mailto:${platform?.supportEmailAddress || ''}`}
							className="d-inline-flex align-items-center text-info text-nowrap mr-auto"
							style={{ padding: '1.2rem 2.4rem', gap: 8 }}
						>
							<ContactUsIcon className="flex-shrink-0" />
							Contact Us
						</a>
						<Button
							variant="outline-primary"
							onClick={onBack}
							className="d-flex justify-content-center align-items-center text-nowrap"
							style={{ flex: '1 1 0', minWidth: 0 }}
							disabled={isLoading}
						>
							Back
						</Button>
						<LoadingButton
							isLoading={isLoading}
							disabled={codeInputValue.length !== 6}
							variant="primary"
							type="submit"
							className="d-flex justify-content-center align-items-center text-nowrap"
							style={{ flex: '1 1 0', minWidth: 0 }}
						>
							Save
						</LoadingButton>
					</div>
				</Modal.Footer>
			</Form>
		</Modal>
	);
};
